import { useState } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Eye, EyeOff, ArrowRight, Sparkles } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import AuthShell from '../components/auth/AuthShell'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)

  const { signIn, currentUser, role, loading: authLoading, signOut, profileError } = useAuth()
  const location = useLocation()

  const from = location.state?.from?.pathname || '/'

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b0c10]">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  if (currentUser && role) {
    if (role === 'customer') {
      return <Navigate to="/customer" replace />
    }
    return <Navigate to={from} replace />
  }

  if (currentUser && !role) {
    return (
      <AuthShell
        title="Almost there"
        subtitle="Your account signed in, but your profile still needs a moment."
      >
        <div className="space-y-4 text-center">
          <h2 className="text-xl font-semibold tracking-tight">Account profile incomplete</h2>
          <p className="text-sm text-muted-foreground">
            {profileError ||
              'Signed in, but your role could not be loaded. Sign out and try again, or ask an admin to check your user record.'}
          </p>
          <Button
            type="button"
            className="w-full"
            style={{ background: '#f0812f', color: '#0b0c10' }}
            onClick={async () => {
              try {
                await signOut()
              } finally {
                window.location.assign('/login')
              }
            }}
          >
            Sign out and retry
          </Button>
        </div>
      </AuthShell>
    )
  }

  async function handleSubmit(e) {
    e.preventDefault()

    if (!email || !password) {
      toast.error('Please fill in all fields')
      return
    }

    setLoading(true)
    try {
      await signIn(email, password)
    } catch {
      toast.error('Failed to sign in. Please check your credentials.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell
      title="Welcome to TrendSetter"
      subtitle="Sign in for washes, QR check-in, subscriptions, and loyalty, or create a free account to get started."
    >
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-foreground">Sign in</h2>
          <p className="mt-1 text-sm text-muted-foreground">Use your email and password</p>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 pr-11"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="flex justify-end">
            <Link
              to="/forgot-password"
              className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Forgot password?
            </Link>
          </div>

          <Button
            type="submit"
            className="h-11 w-full text-base font-semibold"
            size="lg"
            disabled={loading}
            style={{ background: '#f0812f', color: '#0b0c10' }}
          >
            {loading ? (
              <>
                <LoadingSpinner size="small" className="mr-2" />
                Signing in…
              </>
            ) : (
              <>
                Sign in
                <ArrowRight className="size-4" />
              </>
            )}
          </Button>
        </form>

        <div
          className="rounded-xl border-2 bg-white p-4 sm:p-5"
          style={{ borderColor: 'rgba(240,129,47,0.45)' }}
        >
          <div className="mb-3 flex items-start gap-2">
            <Sparkles className="mt-0.5 size-4 shrink-0" style={{ color: '#f0812f' }} />
            <div>
              <p className="font-semibold text-foreground">New here?</p>
              <p className="text-sm text-muted-foreground">
                Create a free customer account for QR check-in and wash history.
              </p>
            </div>
          </div>
          <Link
            to="/register"
            className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-lg border-2 text-base font-semibold transition-colors hover:bg-[#f0812f]/10"
            style={{ borderColor: '#f0812f', color: '#c45f12' }}
          >
            Create account
          </Link>
        </div>
      </div>
    </AuthShell>
  )
}
